import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Filter } from '../components/DataGrid';

export interface SearchQuery {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters: unknown[];
  search?: string;
  setSearchQuery: (newQuery: Omit<SearchQuery, 'setSearchQuery'>) => void;
}

const useSearchQuery = (): SearchQuery => {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo<SearchQuery>(() => {
    const setSearchQuery: SearchQuery['setSearchQuery'] = (newQuery) => {
      Object.entries(newQuery).forEach(([key, val]) => {
        if (!val) {
          searchParams.delete(key);
        } else if (Array.isArray(val) && val.length === 0) {
          searchParams.delete(key);
        } else if (typeof val === 'object') {
          searchParams.set(key, JSON.stringify(val));
        } else {
          searchParams.set(key, val);
        }
      });

      // console.log('set2', searchParams);

      setSearchParams(searchParams, { replace: true });
    };
    const search = searchParams.get('search') || undefined;
    const sortBy = searchParams.get('sortBy') || undefined;
    const sortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc';

    // Try to parse filters from new JSON array format
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      try {
        const decoded = decodeURIComponent(filtersParam);
        const parsed = JSON.parse(decoded);
        if (Array.isArray(parsed)) {
          return {
            search,
            sortBy,
            sortOrder,
            filters: parsed,
            setSearchQuery,
          };
        }
      } catch (error) {
        console.warn('Failed to parse filters from URL, falling back to legacy format', error);
      }

      // If filters param exists but parsing failed, return without filters
      return { search, sortBy, sortOrder, filters: [], setSearchQuery };
    }

    // Legacy format: individual query params as filters
    const params = Array.from(searchParams.entries());
    const { sortBy: _, sortOrder: __, filters, ...legacyFilters } = Object.fromEntries(params);

    const filterParams = Object.entries(legacyFilters);
    const filter: Filter = filterParams.reduce<Filter>((result, [name, value], i) => {
      let operator = '=';
      if (value.startsWith('>')) {
        operator = '>';
        value = value.substring(1);
      }
      if (value.startsWith('<')) {
        operator = '<';
        value = value.substring(1);
      }
      const itemFilter: [string, string, any] = [name, operator, value];
      if (i === 0) {
        return [itemFilter];
      }
      if (i === 1) {
        return [result[0] as [string, string, any], 'AND', itemFilter];
      }
      return [...result, 'AND', itemFilter];
    }, []);

    return {
      search,
      sortBy,
      sortOrder,
      filters: filter,
      setSearchQuery,
    };
  }, [searchParams]);

  return query;
};

export default useSearchQuery;
